import { Search } from '@mui/icons-material';
import { Box, ClickAwayListener, debounce, IconButton, InputAdornment, Menu, MenuItem, TextField } from '@mui/material';
import React from 'react';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { useTranslation } from 'react-i18next';
import { RequestResponseWithBuildingDTO } from '../../../../ApiOld/Api/ApiServerVov';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

export interface IMobileRequestSearchProps {
    data: RequestResponseWithBuildingDTO[];
    setData: (data: RequestResponseWithBuildingDTO[]) => void;
    propsToFilter: { name: string, type: 'string' | 'date' }[];
    propsToSort: { name: string, displayName: string }[];
}

const MobileRequestSearch: React.FC<IMobileRequestSearchProps> = ({ data, propsToFilter, propsToSort, setData }) => {
    const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
    const [searchQuery, setSearchQuery] = React.useState('');
    const [sortConfig, setSortConfig] = React.useState<{ field?: string; direction: 'asc' | 'desc' | 'none' }>({
        direction: 'none',
    });
    const { i18n, t } = useTranslation();

    const [filteredItems, setFilteredItems] = React.useState(data);

    React.useEffect(() => {
        setData(filteredItems);
        applySort();
    }, [filteredItems]);

    const removeDiacritics = (str: string): string => {
        return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    };

    const debouncedSearch = React.useCallback(
        debounce((query: string) => {
            const normalizedQuery = removeDiacritics(query.toLowerCase());

            const filtered = data.filter((item) =>
                propsToFilter.some((prop) => {
                    if (prop.type === 'date') {
                        const formattedDate = new Date(item[prop.name] as string)
                            .toLocaleDateString(i18n.language)
                            .toLowerCase();
                        const normalizedDate = removeDiacritics(formattedDate);
                        return normalizedDate.includes(normalizedQuery);
                    } else {
                        const value = String(item[prop.name] || '').toLowerCase();
                        const normalizedValue = removeDiacritics(value);
                        return normalizedValue.includes(normalizedQuery);
                    }
                })
            );
            setFilteredItems(filtered);
        }, 500),
        [data, propsToFilter, i18n.language, setData]
    );

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value.toLowerCase();
        setSearchQuery(e.target.value);
        debouncedSearch(query);
    };

    const handleCloseMenu = () => {
        setMenuAnchor(null);
    };

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleSortClick = (field: string) => {
        console.log('sort: ', field);
        setSortConfig((prev) => {
            if (prev.field === field) {
                if (prev.direction === 'asc') return { field, direction: 'desc' };
                if (prev.direction === 'desc') return { field: undefined, direction: 'none' };
            }
            return { field, direction: 'asc' };
        });
        localStorage.setItem('requestSort', JSON.stringify({ field, direction: 'asc' }));
    };

    const applySort = React.useCallback(() => {
        if (sortConfig.field && sortConfig.direction !== 'none') {
            setFilteredItems((prev) => {
                return [...prev].sort((a, b) => {
                    if (sortConfig.field) {
                        // Check if this is a date field by name
                        const isDateField = sortConfig.field.toLowerCase().includes('date') ||
                            sortConfig.field.toLowerCase().includes('time');

                        if (isDateField) {
                            // Handle date comparison correctly
                            const aValue = a[sortConfig.field] ? new Date(a[sortConfig.field] as string).getTime() : 0;
                            const bValue = b[sortConfig.field] ? new Date(b[sortConfig.field] as string).getTime() : 0;

                            return sortConfig.direction === 'asc'
                                ? aValue - bValue
                                : bValue - aValue;
                        } else {
                            // Handle string comparison for non-date fields
                            const aValue = String(a[sortConfig.field] || '');
                            const bValue = String(b[sortConfig.field] || '');

                            if (sortConfig.direction === 'asc') return aValue.localeCompare(bValue);
                            if (sortConfig.direction === 'desc') return bValue.localeCompare(aValue);
                        }
                    }
                    return 0;
                });
            });
        } else {
            // Reset to original data when no sorting is applied
            setFilteredItems(data.filter(item =>
                !searchQuery || propsToFilter.some(prop => {
                    const value = String(item[prop.name] || '').toLowerCase();
                    return removeDiacritics(value).includes(removeDiacritics(searchQuery.toLowerCase()));
                })
            ));
        }
    }, [sortConfig, data, searchQuery, propsToFilter]);

    /* const applySort = React.useCallback(() => {
        console.log('sorting ,,,,2');
        if (sortConfig.field && sortConfig.direction !== 'none') {
            console.log('sorting ,,,,3', [...filteredItems].sort((a, b) => {
                if (sortConfig.field) {
                    const aValue = String(a[sortConfig.field] || '');
                    const bValue = String(b[sortConfig.field] || '');
                    if (sortConfig.direction === 'asc') return aValue.localeCompare(bValue);
                    if (sortConfig.direction === 'desc') return bValue.localeCompare(aValue);
                }
                return 0;
            }));
            setFilteredItems((prev) => {
                return [...prev].sort((a, b) => {
                    if (sortConfig.field) {
                        const aValue = String(a[sortConfig.field] || '');
                        const bValue = String(b[sortConfig.field] || '');
                        if (sortConfig.direction === 'asc') return aValue.localeCompare(bValue);
                        if (sortConfig.direction === 'desc') return bValue.localeCompare(aValue);
                    }
                    return 0;
                });
            });
        } else {
            console.log('sorting ,,,,4');
            setFilteredItems(data);
        }
    }, [sortConfig, data, setFilteredItems]); */

    React.useEffect(() => {
        let sort = localStorage.getItem('requestSort');
        if (sort) {
            setSortConfig(JSON.parse(sort!));
        }
    }, []);

    React.useEffect(() => {
        applySort();
    }, [sortConfig]);

    return (
        <Box display="flex" alignItems="center" mb={2}>
            <TextField
                size="small"
                variant="outlined"
                placeholder={'Hledat'}
                value={searchQuery}
                onChange={handleSearch}
                sx={{ flexGrow: 1 }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton sx={{ height: 30, width: 30 }}>
                                <Search />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}

            />
            <ClickAwayListener onClickAway={handleCloseMenu}>
                <Box>
                    <IconButton sx={{ background: sortConfig.direction !== 'none' ? '#ffc600' : 'transparent', borderRadius: 5, width: 30, height: 30, ml: 3 }} onClick={handleOpenMenu}>
                        <SwapVertIcon />
                    </IconButton>
                    <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={handleCloseMenu}>
                        {propsToSort.map((prop) => (
                            <MenuItem key={String(prop.name)} onClick={() => handleSortClick(prop.name)}>
                                <span>{prop.displayName}</span>
                                <Box ml={1}>
                                    {sortConfig.field === prop.name && sortConfig.direction !== 'none' ? (
                                        sortConfig.direction === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />
                                    ) : <></>}
                                </Box>
                            </MenuItem>
                        ))}
                    </Menu>
                </Box>
            </ClickAwayListener>
        </Box>
    );
};

export default MobileRequestSearch;